import React, { useState, useEffect, useMemo } from "react";
import { useUnsavedChangesGuard } from "@/hooks/useUnsavedChangesGuard";
import { useOfflineCatalogCache } from "@/hooks/useOfflineCatalogCache";
import { useGiroOrderItems } from "@/hooks/useGiroOrderItems";
import { useGiroTotals } from "@/hooks/useGiroTotals";
import { useAuth } from "@/App";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Lock, Search, Plus, FileText, X } from "lucide-react";
import { toast } from "sonner";
import { GiriListView } from "./giri/GiriListView";
import { StopListView } from "./giri/StopListView";
import { StopHeader } from "./giri/StopHeader";
import { StopProductsList } from "./giri/StopProductsList";
import { OrderBottomBar } from "./giri/OrderBottomBar";
import { AddProductModal } from "./giri/AddProductModal";
import { GiroStopBanners } from "./giri/GiroStopBanners";
import { EstrattoContoModal } from "@/components/EstrattoContoModal";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { submitOrderWithOfflineFallback } from "@/utils/pendingOrderSync";
import { maybeCapturePosition } from "@/utils/geo";
import { checkOmaggioConflict } from "@/utils/omaggiAlert";

export default function GiriOrdineRapido() {
  const { user, authAxios } = useAuth();
  const navigate = useNavigate();
  const offlineCatalog = useOfflineCatalogCache(authAxios);
  const orderItemsHook = useGiroOrderItems();
  const {
    orderItems,
    setOrderItems,
    lastActivatedCod,
    consumeLastActivated,
    reset: resetOrderItems,
    hasUnsaved: hasUnsavedItems,
    toggle: toggleProduct,
    updateQty,
    updateOmaggio,
    updateNote: updateItemNote,
    updateDiscount: updateItemDiscount,
  } = orderItemsHook;

  const [giri, setGiri] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedGiro, setSelectedGiro] = useState(null);
  const [stops, setStops] = useState([]);
  const [selectedStop, setSelectedStop] = useState(null);
  const [stopProducts, setStopProducts] = useState([]);
  const [extraProducts, setExtraProducts] = useState([]);
  const [editingOrderId, setEditingOrderId] = useState(null);
  const [lockedOrder, setLockedOrder] = useState(null);
  const [orderNote, setOrderNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [searchFilter, setSearchFilter] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [catalogSearch, setCatalogSearch] = useState("");
  const [catalogResults, setCatalogResults] = useState([]);
  const [catalogSearching, setCatalogSearching] = useState(false);
  const [showOther, setShowOther] = useState(false);
  // Offline prefetch progress (per giro day)
  const [prefetchState, setPrefetchState] = useState({ active: false, done: 0, total: 0, ready: false });
  const [showNoteFor, setShowNoteFor] = useState(null);
  const [showDiscountFor, setShowDiscountFor] = useState(null);
  const [clientDiscounts, setClientDiscounts] = useState({});
  const [showEstrattoConto, setShowEstrattoConto] = useState(false);
  const [inlineCatalogResults, setInlineCatalogResults] = useState([]);
  const [inlineCatalogSearching, setInlineCatalogSearching] = useState(false);

  useEffect(() => {
    authAxios.get("/giri").then(r => setGiri(r.data)).catch(() => toast.error("Errore caricamento giri")).finally(() => setLoading(false));
  }, [authAxios]);

  const loadGiro = async (cod_agente, cod_giorno) => {
    setSelectedGiro({ cod_agente, cod_giorno });
    setSelectedStop(null);
    setStopProducts([]);
    setOrderItems({});
    setPrefetchState({ active: false, done: 0, total: 0, ready: false });
    try {
      const res = await authAxios.get(`/giri/${cod_agente}/${cod_giorno}`);
      setStops(res.data);
      // Offline pre-fetch: warm the Service Worker cache with the products endpoint for EVERY
      // stop of the day. This way the agent can open any client when offline and immediately
      // see the product list without network. Runs in background, low-priority.
      if (navigator.onLine && res.data && res.data.length) {
        prefetchAllStopsForOffline(cod_agente, cod_giorno, res.data);
      }
      return res.data;
    } catch {
      toast.error("Errore caricamento fermate");
      return [];
    }
  };

  // Pre-warm SW cache: serially fetch products for each stop with a small delay to avoid
  // saturating the connection / mobile data. Failures are silently ignored.
  const prefetchAllStopsForOffline = async (cod_agente, cod_giorno, allStops) => {
    const targets = allStops.filter(s => !s.completed_order_id);
    if (targets.length === 0) {
      setPrefetchState({ active: false, done: 0, total: 0, ready: true });
      // Even with 0 targets, refresh the catalog snapshot for offline search
      offlineCatalog.refresh();
      return;
    }
    setPrefetchState({ active: true, done: 0, total: targets.length, ready: false });
    let done = 0;
    for (const s of targets) {
      try {
        await authAxios.get(`/giri/${cod_agente}/${cod_giorno}/${s.id}/prodotti`);
        done += 1;
        setPrefetchState({ active: true, done, total: targets.length, ready: false });
        // Tiny delay to keep the UI responsive
        await new Promise(r => setTimeout(r, 50));
      } catch (err) {
        console.debug("[GiriOrdineRapido] prefetch stopped:", err?.message || err);
        // Mark as inactive but not ready (partial) — user can still see progress
        setPrefetchState({ active: false, done, total: targets.length, ready: false });
        return;
      }
    }
    // After warming stop products, also fetch the FULL catalog snapshot for offline search.
    await offlineCatalog.refresh();
    setPrefetchState({ active: false, done, total: targets.length, ready: true });
    console.log(`[GiriOrdineRapido] Offline cache warmed: ${done}/${targets.length} stops + catalog`);
  };

  // Used by GiriListView search: given a stop search result, jump directly to its stop view.
  const handleJumpToStop = async (searchResult) => {
    const giroCoord = { cod_agente: searchResult.cod_agente, cod_giorno: searchResult.cod_giorno };
    const stopsLoaded = await loadGiro(searchResult.cod_agente, searchResult.cod_giorno);
    const target = stopsLoaded.find((s) => s.id === searchResult.id);
    if (target) {
      await loadStopProducts(target, true, giroCoord);
    } else {
      toast.error("Fermata non trovata nel giro");
    }
  };

  const confirmLeaveStop = () => {
    if (!hasUnsavedItems()) return true;
    return window.confirm("Ci sono articoli inseriti non salvati. Uscire comunque? L'ordine non verrà creato.");
  };

  // Guard "modifiche non salvate" per tasto indietro Android / chiusura tab
  useUnsavedChangesGuard(
    selectedStop && hasUnsavedItems() && !lockedOrder,
    "Hai articoli inseriti non ancora salvati. Vuoi davvero uscire? L'ordine non verrà creato."
  );

  const loadStopProducts = async (stop, skipConfirm = false, giroOverride = null) => {
    if (!skipConfirm && selectedStop && !confirmLeaveStop()) return;
    setSelectedStop(stop);
    setOrderItems({});
    setExtraProducts([]);
    setShowOther(false);
    setEditingOrderId(null);
    setLockedOrder(null);
    setOrderNote("");
    setShowNoteFor(null);
    setShowDiscountFor(null);
    setClientDiscounts({});
    // Omaggi recenti del cliente (alert "riproposta di reso", finestra 18 gg)
    // Filtra per destinazione: per clienti multi-punto vendita un omaggio
    // dato a un'altra destinazione NON deve scattare l'alert qui.
    setRecentOmaggi({});
    if (stop.client_id) {
      const params = {};
      if (stop.cod_destin != null && stop.cod_destin !== "") {
        params.cod_destin = String(stop.cod_destin);
      }
      authAxios
        .get(`/clients/${stop.client_id}/recent-omaggi`, { params })
        .then((r) => setRecentOmaggi(r.data?.omaggi || {}))
        .catch(() => {});
    }
    // Use override (coming from search-jump) or selectedGiro to compose the URL.
    // giroOverride is needed because setSelectedGiro() is async: when we navigate
    // from search, selectedGiro isn't yet updated when this function runs.
    const giro = giroOverride || selectedGiro;
    try {
      const res = await authAxios.get(`/giri/${giro.cod_agente}/${giro.cod_giorno}/${stop.id}/prodotti`);
      setStopProducts(res.data);

      // Precarica sconti cliente via /resolve-prices (non bloccante)
      // Con cache localStorage per uso offline: la risposta viene salvata e riusata
      // automaticamente quando la rete fallisce (TTL 7 giorni).
      if (stop.client_id) {
        const pids = (res.data || []).map(p => p.product_id).filter(Boolean);
        if (pids.length > 0) {
          const cacheKey = `resolve_prices_${stop.client_id}`;
          const skuByPid = new Map((res.data || []).map(p => [p.product_id, p.cod_articolo]));
          const buildMap = (pricesObj) => {
            const map = {};
            for (const [pid, v] of Object.entries(pricesObj || {})) {
              const sku = skuByPid.get(pid);
              if (!sku) continue;
              map[sku] = {
                sc1: Number(v?.sc1 || 0),
                sc2: Number(v?.sc2 || 0),
                sc3: Number(v?.sc3 || 0),
                sc4: Number(v?.sc4 || 0),
                source: v?.source || null,
                manual_price: v?.source === "manual" ? Number(v?.price || 0) : null,
                promo_net: (v?.source === "promo" && v?.promo_net_fixed) ? Number(v?.price || 0) : null,
                promo_descr: v?.promo_descr || null,
              };
            }
            return map;
          };
          authAxios
            .post(`/clients/${stop.client_id}/resolve-prices`, { product_ids: pids })
            .then((rp) => {
              const map = buildMap(rp.data?.prices);
              setClientDiscounts(map);
              // Save in localStorage for offline fallback (TTL 7 days)
              try {
                localStorage.setItem(cacheKey, JSON.stringify({
                  ts: Date.now(),
                  prices: rp.data?.prices || {},
                }));
              } catch (e) {
                // localStorage may be full — non-fatal
                console.debug("[GiriOrdineRapido] resolve-prices cache write failed:", e?.message);
              }
            })
            .catch((err) => {
              console.warn("[GiriOrdineRapido] resolve-prices preload failed (offline fallback active):", err?.response?.data?.detail || err?.message || err);
              // Offline fallback: use cached prices if available and not too old (7 days)
              try {
                const raw = localStorage.getItem(cacheKey);
                if (raw) {
                  const parsed = JSON.parse(raw);
                  const ageMs = Date.now() - (parsed.ts || 0);
                  if (ageMs < 7 * 24 * 3600 * 1000) {
                    const map = buildMap(parsed.prices);
                    setClientDiscounts(map);
                    console.log("[GiriOrdineRapido] using cached client discounts (offline)");
                  }
                }
              } catch (e) {
                console.debug("[GiriOrdineRapido] resolve-prices cache read failed:", e?.message);
              }
            });
        }
      }

      // If this stop already has an order collegato, verify its owner/status
      if (stop.completed_order_id) {
        try {
          const oRes = await authAxios.get(`/orders/${stop.completed_order_id}`);
          const existing = oRes.data;
          const preload = {};
          const extras = [];
          const stopSkus = new Set((res.data || []).map(p => p.cod_articolo));
          for (const it of existing.items || []) {
            const sku = it.product_sku || "";
            if (!sku) continue;
            const hasOvr = ((it.sc1 || 0) + (it.sc2 || 0) + (it.sc3 || 0) + (it.sc4 || 0)) > 0;
            preload[sku] = {
              qty: Number(it.quantity) || 0,
              qty_om: Number(it.qty_omaggio) || 0,
              note: it.note || "",
              ...(hasOvr ? { discount_override: {
                sc1: Number(it.sc1 || 0),
                sc2: Number(it.sc2 || 0),
                sc3: Number(it.sc3 || 0),
                sc4: Number(it.sc4 || 0),
              } } : {}),
            };
            if (!stopSkus.has(sku)) {
              // Recupera la macro categoria dal catalogo cached (serve per non
              // mostrare il warning "⚠️ PREZZO" sui Prodotti Triolo)
              const catMatch = offlineCatalog.search(sku).find(p => p.sku === sku);
              extras.push({
                cod_articolo: sku,
                name: it.product_name,
                product_id: it.product_id,
                price_list: Number(it.price_unit) || 0,
                unit: it.unit || "pz",
                iva_percentage: it.iva_percentage || 10,
                macro_category: catMatch?.macro_category || null,
                non_disponibile: false,
                ultima_qta: 0,
                ultima_data: null,
                _extra: true,
              });
            }
          }
          setExtraProducts(extras);
          setOrderItems(preload);
          setOrderNote(existing.note || "");

          if (existing?.status === "da_trasferire" && existing.created_by === user?.id) {
            setEditingOrderId(existing.id);
            toast.info(`Modifica ordine ${existing.numero}`, { duration: 2500 });
          } else {
            setLockedOrder({
              order: existing,
              reason: existing.status === "da_trasferire"
                ? "Ordine di un altro agente"
                : "Ordine già trasferito",
            });
          }
        } catch (err) {
          if (err?.response?.status === 404) {
            setLockedOrder({
              order: null,
              reason: "Ordine già inserito, non modificabile",
            });
          }
        }
      }
      return res.data;
    } catch {
      toast.error("Errore caricamento prodotti");
      return null;
    }
  };

  // ====== PREFILL DA ORDINE VOCALE ======
  // La pagina Ordini Vocali salva in sessionStorage le righe dettate e naviga
  // qui: apriamo direttamente la fermata con gli articoli già selezionati.
  useEffect(() => {
    const raw = sessionStorage.getItem("voice_order_prefill");
    if (!raw) return;
    sessionStorage.removeItem("voice_order_prefill");
    let prefill;
    try { prefill = JSON.parse(raw); } catch { return; }
    if (!prefill?.stop_id || !prefill?.rows?.length) return;
    (async () => {
      const giroCoord = { cod_agente: prefill.cod_agente, cod_giorno: prefill.cod_giorno };
      const stopsLoaded = await loadGiro(prefill.cod_agente, prefill.cod_giorno);
      const target = (stopsLoaded || []).find((s) => s.id === prefill.stop_id);
      if (!target) {
        toast.error("Fermata del giro non trovata");
        return;
      }
      if (target.completed_order_id) {
        toast.error("Fermata già completata: esiste già un ordine");
        return;
      }
      const prods = await loadStopProducts(target, true, giroCoord);
      applyVoicePrefill(prefill, target, prods || []);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyVoicePrefill = (prefill, stop, prods) => {
    const canale = Number(stop.sconto_canale) || 0;
    const entries = {};
    const extras = [];
    for (const r of prefill.rows) {
      if (!r.sku) continue;
      const inStop = prods.find((p) => p.cod_articolo === r.sku);
      if (!inStop) {
        extras.push({
          cod_articolo: r.sku,
          name: r.name,
          product_id: r.product_id,
          price_list: Number(r.price_list) || 0,
          unit: r.unit || "pz",
          iva_percentage: r.iva_percentage || 10,
          macro_category: r.macro_category || null,
          non_disponibile: false,
          ultima_qta: 0,
          ultima_data: null,
          ultimo_prezzo: Number(r.ultimo_prezzo) || 0,
          _extra: true,
        });
      }
      const prev = entries[r.sku];
      const entry = {
        qty: (prev?.qty || 0) + (Number(r.qty) || 0),
        qty_om: (prev?.qty_om || 0) + (Number(r.qty_om) || 0),
        note: prev?.note || "",
      };
      // "prezzo pagato" / prezzo esplicito dettato → override come il tasto "Ult. €"
      // del pannello sconti: sc1 calcolato per ottenere il netto target (canale incluso)
      const priceList = Number(inStop ? inStop.price_list : r.price_list) || 0;
      let targetNetto = null;
      if (r.price_mode === "pagato") {
        targetNetto = Number((inStop ? inStop.ultimo_prezzo : r.ultimo_prezzo) || 0);
        if (!(targetNetto > 0)) {
          toast.warning(`"${r.name}": nessun prezzo pagato in storico, resta il listino`);
        }
      } else if (r.price_mode === "explicit") {
        targetNetto = Number(r.price_value) || 0;
      }
      if (targetNetto > 0 && priceList > 0) {
        const denom = priceList * (1 - canale / 100);
        if (denom > 0) {
          let sc1 = (1 - targetNetto / denom) * 100;
          sc1 = Math.max(0, Math.min(100, Math.round(sc1 * 100) / 100));
          entry.discount_override = { sc1, sc2: 0, sc3: 0, sc4: 0 };
        }
      }
      entries[r.sku] = entry;
    }
    if (extras.length > 0) setExtraProducts((prev) => [...extras, ...prev]);
    setOrderItems(entries);
    if (prefill.transcript) {
      setOrderNote(`🎤 Ordine vocale: "${prefill.transcript}"`);
    }
    const n = Object.keys(entries).length;
    toast.success(`Dettatura applicata: ${n} articol${n === 1 ? "o" : "i"} selezionat${n === 1 ? "o" : "i"} — controlla e crea l'ordine`);
  };

  // Cerca prodotti nel catalogo per aggiungere articoli fuori lista abituale
  useEffect(() => {
    if (!showAddModal) return;
    const q = catalogSearch.trim();
    if (q.length < 2) { setCatalogResults([]); return; }
    setCatalogSearching(true);
    const t = setTimeout(async () => {
      try {
        const res = await authAxios.get(`/products?search=${encodeURIComponent(q)}`);
        setCatalogResults(res.data.slice(0, 50));
      } catch {
        // Offline fallback: search the cached catalog snapshot locally
        const local = offlineCatalog.search(q).slice(0, 50);
        setCatalogResults(local);
      } finally {
        setCatalogSearching(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [catalogSearch, showAddModal, authAxios]);

  // Ricerca inline nel catalogo: quando l'agente digita nella barra "Cerca prodotto"
  // e c'è una fermata aperta, cerchiamo anche tra TUTTI i prodotti del catalogo,
  // escludendo quelli già in stopProducts/extraProducts/orderItems. Cosi può aggiungere
  // articoli "fuori lista" senza dover aprire il modale + Articolo.
  useEffect(() => {
    if (!selectedStop || lockedOrder) {
      setInlineCatalogResults([]);
      return undefined;
    }
    const q = searchFilter.trim();
    if (q.length < 2) {
      setInlineCatalogResults([]);
      setInlineCatalogSearching(false);
      return undefined;
    }
    setInlineCatalogSearching(true);
    const t = setTimeout(async () => {
      const knownSkus = new Set([
        ...stopProducts.map(p => p.cod_articolo),
        ...extraProducts.map(p => p.cod_articolo),
      ]);
      try {
        const res = await authAxios.get(`/products?search=${encodeURIComponent(q)}`);
        const filtered = (res.data || [])
          .filter(p => p.sku && !knownSkus.has(p.sku))
          .slice(0, 20);
        setInlineCatalogResults(filtered);
      } catch {
        // Offline fallback: search the cached catalog snapshot locally
        const local = offlineCatalog.search(q)
          .filter(p => p.sku && !knownSkus.has(p.sku))
          .slice(0, 20);
        setInlineCatalogResults(local);
      } finally {
        setInlineCatalogSearching(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [searchFilter, selectedStop, lockedOrder, stopProducts, extraProducts, authAxios]);

  const addExtraProduct = (catalogProd, qty = 1) => {
    const existsInStop = stopProducts.some(p => p.cod_articolo === catalogProd.sku);
    const existsInExtras = extraProducts.some(p => p.cod_articolo === catalogProd.sku);
    if (existsInStop || existsInExtras) {
      toast.info("Articolo già in lista");
      return;
    }
    const safeQty = Math.max(1, Number(qty) || 1);
    const newExtra = {
      cod_articolo: catalogProd.sku,
      name: catalogProd.name,
      product_id: catalogProd.id,
      price_list: catalogProd.price_list || 0,
      price_min: catalogProd.price_min,
      price_auth: catalogProd.price_auth,
      price_promo: catalogProd.price_promo,
      macro_category: catalogProd.macro_category,
      unit: catalogProd.unit || "pz",
      iva_percentage: catalogProd.iva_percentage || 10,
      non_disponibile: catalogProd.non_disponibile || false,
      ultima_qta: 0,
      ultima_data: null,
      _extra: true,
    };
    setExtraProducts(prev => [newExtra, ...prev]);
    setOrderItems(prev => ({ ...prev, [catalogProd.sku]: { qty: safeQty, qty_om: 0, note: "" } }));
    // After confirming the catalog item, clear the search filter so the user
    // immediately sees the new article among the rest of the products.
    setSearchFilter("");
    setInlineCatalogResults([]);
    toast.success(`Aggiunto: ${catalogProd.name} × ${safeQty}`);
  };

  const { totalQty, totalOm, selectedCount, totalStima, noPriceCount, marginEur, marginPct } = useGiroTotals({
    orderItems,
    stopProducts,
    extraProducts,
    selectedStop,
    clientDiscounts,
    lockedOrder,
  });

  // Alert vocale per articoli senza prezzo: "Conferma i prezzi per favore"
  const [showPriceConfirm, setShowPriceConfirm] = useState(false);
  // Alert "riproposta di reso": omaggio uguale/simile a uno recente (18 gg)
  const [recentOmaggi, setRecentOmaggi] = useState({});
  const [showOmaggiConfirm, setShowOmaggiConfirm] = useState(false);
  const speakAlert = (text) => {
    try {
      if (navigator.vibrate) navigator.vibrate([250, 100, 250]);
      if ("speechSynthesis" in window) {
        window.speechSynthesis.cancel();
        const u = new SpeechSynthesisUtterance(text);
        u.lang = "it-IT";
        u.rate = 0.95;
        u.volume = 1.0;
        window.speechSynthesis.speak(u);
      }
    } catch { /* audio non disponibile: resta il popup */ }
  };

  const omaggiConflicts = useMemo(() => {
    const all = [...stopProducts, ...extraProducts];
    const out = [];
    for (const [cod, e] of Object.entries(orderItems)) {
      const om = Number(e?.qty_om || 0);
      if (om <= 0) continue;
      const prod = all.find((p) => p.cod_articolo === cod);
      const conflict = checkOmaggioConflict(prod, om, recentOmaggi[cod]);
      if (conflict) out.push({ cod, name: prod?.name || cod, om, prev: conflict });
    }
    return out;
  }, [orderItems, recentOmaggi, stopProducts, extraProducts]);

  const saveOrder = async (skipPriceCheck) => {
    if (!selectedStop || selectedCount === 0) return;
    if (skipPriceCheck !== true) {
      // 1) Articoli senza prezzo indicato → allarme vocale + conferma esplicita
      if (noPriceCount > 0) {
        speakAlert("Conferma i prezzi per favore");
        setShowPriceConfirm(true);
        return;
      }
      // 2) Omaggi simili a omaggi recenti → possibile riproposta di reso
      if (omaggiConflicts.length > 0) {
        speakAlert("Attenzione, controlla gli omaggi: possibile reso riproposto");
        setShowOmaggiConfirm(true);
        return;
      }
    }
    setSaving(true);
    try {
      const allProducts = [...stopProducts, ...extraProducts];
      const items = [];
      for (const [cod, entry] of Object.entries(orderItems)) {
        const qty = entry?.qty || 0;
        const qty_om = entry?.qty_om || 0;
        if (qty <= 0 && qty_om <= 0) continue;
        const prod = allProducts.find(p => p.cod_articolo === cod);
        if (!prod || !prod.product_id) continue;
        const ovr = entry?.discount_override;
        items.push({
          product_id: prod.product_id,
          product_name: prod.name,
          product_sku: prod.cod_articolo,
          quantity: qty,
          qty_omaggio: qty_om,
          unit: prod.unit || "pz",
          price_unit: prod.price_list || 0,
          sc1: ovr ? Number(ovr.sc1 || 0) : 0,
          sc2: ovr ? Number(ovr.sc2 || 0) : 0,
          sc3: ovr ? Number(ovr.sc3 || 0) : 0,
          sc4: ovr ? Number(ovr.sc4 || 0) : 0,
          iva_percentage: prod.iva_percentage || 10,
          note: (entry?.note || "").trim() || null,
          _override: !!ovr,
        });
      }

      let createdOrder = null;
      let queuedOffline = false;
      if (selectedStop.client_id) {
        let sc = selectedStop.sconto_canale || 0;
        try {
          const resolved = await authAxios.post(`/clients/${selectedStop.client_id}/resolve-prices`, {
            product_ids: items.map(i => i.product_id)
          });
          const prices = resolved.data.prices || {};
          sc = resolved.data.sconto_canale || 0;
          for (const item of items) {
            if (item._override) continue;
            const rp = prices[item.product_id];
            if (rp?.source === "rule" || rp?.source === "promo") {
              if (rp.promo_net_fixed) {
                // Promo a prezzo netto fisso: salviamo come price_unit, sconti azzerati
                item.price_unit = rp.price;
                item.sc1 = 0; item.sc2 = 0; item.sc3 = 0; item.sc4 = 0;
              } else {
                item.sc1 = rp.sc1 || 0;
                item.sc2 = rp.sc2 || 0;
                item.sc3 = rp.sc3 || 0;
                item.sc4 = rp.sc4 || 0;
              }
            } else if (rp?.source === "manual") {
              item.price_unit = rp.price;
            }
          }
        } catch (err) {
          console.warn("[GiriOrdineRapido] resolve-prices failed, using list prices:", err?.response?.data?.detail || err?.message || err);
        }
        for (const item of items) delete item._override;

        const finalNote = (orderNote || "").trim();

        if (editingOrderId) {
          const resp = await authAxios.put(`/orders/${editingOrderId}/items`, { items });
          try {
            const current = resp.data;
            await authAxios.put(`/orders/${editingOrderId}`, {
              client_id: current.client_id,
              client_name: current.client_name,
              client_partita_iva: current.client_partita_iva,
              client_indirizzo: current.client_indirizzo,
              client_citta: current.client_citta,
              sconto_canale: current.sconto_canale || 0,
              items,
              note: finalNote,
            });
          } catch (err) {
            console.warn("[GiriOrdineRapido] order header update failed:", err?.response?.data?.detail || err?.message || err);
          }
          createdOrder = resp.data;
        } else {
          const result = await submitOrderWithOfflineFallback(
            authAxios,
            {
              client_id: selectedStop.client_id,
              client_name: selectedStop.ragione_sociale || selectedStop.denominaz_1 || selectedStop.denominaz,
              sconto_canale: sc,
              items,
              giro_provenienza: `${selectedGiro.cod_agente}-${selectedGiro.cod_giorno}`,
              cod_destin: selectedStop.cod_destin ? String(selectedStop.cod_destin) : null,
              note: finalNote,
              ...((await maybeCapturePosition(user)) || {}),
            },
            {
              source: "giri",
              postActions: [{ type: "mark_giro_stop_complete", stop_id: selectedStop.id }],
            }
          );
          if (result.online) {
            createdOrder = result.order;
          } else {
            queuedOffline = true;
          }
        }
      }

      if (createdOrder?.id) {
        // Online path: server-side mark-complete already attempted by post-action, but we still
        // refresh local UI to reflect the completion immediately.
        try {
          await authAxios.put(`/giri/fermata/${selectedStop.id}/complete`, { order_id: createdOrder.id });
        } catch (err) {
          console.warn("[GiriOrdineRapido] mark-stop-completed failed:", err?.response?.data?.detail || err?.message || err);
        }
        setStops(prev => prev.map(s =>
          s.id === selectedStop.id
            ? { ...s, completed_order_id: createdOrder.id, completed_at: new Date().toISOString() }
            : s
        ));
      } else if (queuedOffline) {
        // Mark the stop as queued locally so the agent doesn't re-create the same order.
        setStops(prev => prev.map(s =>
          s.id === selectedStop.id
            ? { ...s, pending_offline: true, completed_at: new Date().toISOString() }
            : s
        ));
      }

      if (queuedOffline) {
        toast.success(`Ordine ${selectedStop.denominaz} salvato offline — sincronizzazione automatica al ritorno della rete`, { duration: 5000 });
      } else {
        toast.success(editingOrderId ? `Ordine aggiornato per ${selectedStop.denominaz}` : `Ordine creato per ${selectedStop.denominaz}`);
      }
      setEditingOrderId(null);
      setOrderItems({});
      setExtraProducts([]);
      setOrderNote("");
      setShowNoteFor(null);
      setShowDiscountFor(null);
      setSelectedStop(null);
      setStopProducts([]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore creazione ordine");
    } finally {
      setSaving(false);
    }
  };

  const { mainProducts, otherProducts } = useMemo(() => {
    const main = [];
    const others = [];
    // Extras (added from catalog search) go on top.
    for (const p of extraProducts) main.push(p);
    for (const p of stopProducts) {
      const selected = orderItems[p.cod_articolo] !== undefined;
      const hasHistory = (p.ultima_qta || 0) > 0 || (p.ultimo_reso_qta || 0) > 0;
      if (selected || hasHistory) main.push(p);
      else others.push(p);
    }
    return { mainProducts: main, otherProducts: others };
  }, [stopProducts, extraProducts, orderItems]);

  const filteredMain = useMemo(() => {
    if (!searchFilter) return mainProducts;
    const q = searchFilter.toLowerCase();
    return mainProducts.filter(p =>
      (p.name || "").toLowerCase().includes(q) || (p.cod_articolo || "").includes(q)
    );
  }, [mainProducts, searchFilter]);

  const filteredOther = useMemo(() => {
    if (!searchFilter) return otherProducts;
    const q = searchFilter.toLowerCase();
    return otherProducts.filter(p =>
      (p.name || "").toLowerCase().includes(q) || (p.cod_articolo || "").includes(q)
    );
  }, [otherProducts, searchFilter]);

  useEffect(() => {
    if (searchFilter.trim().length > 0) setShowOther(true);
  }, [searchFilter]);

  if (loading) return <div className="min-h-screen bg-[#09090b] flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-amber-500" /></div>;

  // VIEW: Giro list
  if (!selectedGiro) {
    return (
      <GiriListView
        giri={giri}
        user={user}
        authAxios={authAxios}
        onSelectGiro={loadGiro}
        onReloadGiri={setGiri}
        onJumpToStop={handleJumpToStop}
      />
    );
  }

  // VIEW: Stop list
  if (!selectedStop) {
    const isAdmin = user?.role === "admin";
    const handleUnlockStop = async (stop) => {
      try {
        await authAxios.put(`/giri/fermata/${stop.id}/uncomplete`, {});
        setStops(prev => prev.map(s =>
          s.id === stop.id
            ? { ...s, completed_order_id: null, completed_at: null, completed_by: null }
            : s
        ));
      } catch (e) {
        alert("Errore sblocco presa ordine: " + (e.response?.data?.detail || e.message));
      }
    };
    return (
      <StopListView
        selectedGiro={selectedGiro}
        stops={stops}
        onBack={() => setSelectedGiro(null)}
        onSelectStop={loadStopProducts}
        isAdmin={isAdmin}
        onUnlockStop={handleUnlockStop}
        prefetchState={prefetchState}
      />
    );
  }

  // VIEW: Product selection for stop
  const currentIdx = stops.findIndex(s => s.id === selectedStop.id);

  return (
    <div className="min-h-screen bg-[#09090b]">
      <main className="p-4 sm:p-6 pb-24">
        <PageHeader title={selectedStop.denominaz} />

        <StopHeader
          selectedStop={selectedStop}
          currentIdx={currentIdx}
          totalStops={stops.length}
          editingOrderId={editingOrderId}
          lockedOrder={lockedOrder}
          onBack={() => {
            if (!confirmLeaveStop()) return;
            setSelectedStop(null); setStopProducts([]); setOrderItems({}); setExtraProducts([]);
            setLockedOrder(null); setOrderNote(""); setShowNoteFor(null); setEditingOrderId(null);
          }}
        />

        {/* Avvisi: articoli senza prezzo + estratto conto */}
        <GiroStopBanners
          noPriceCount={noPriceCount}
          saldoAperto={selectedStop.saldo_aperto}
          nFattureAperte={selectedStop.n_fatture_aperte}
          onOpenEstratto={() => setShowEstrattoConto(true)}
        />

        {/* Lock banner */}
        {lockedOrder && (
          <div className="mb-4 p-3 rounded-lg border border-red-500/30 bg-red-500/5 flex items-start gap-3" data-testid="locked-banner">
            <Lock className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-red-300 text-sm font-medium">{lockedOrder.reason}</p>
              <p className="text-zinc-400 text-xs mt-0.5">
                L&apos;ordine non può essere modificato da qui. Si sbloccherà al prossimo reset settimanale (import giri).
                {lockedOrder.order?.numero && <span className="font-mono ml-1 text-zinc-500">· {lockedOrder.order.numero}</span>}
              </p>
            </div>
          </div>
        )}

        {/* Search + Add button */}
        {!lockedOrder && (
          <div className="flex gap-2 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
              <Input placeholder="Cerca prodotto..." value={searchFilter} onChange={(e) => setSearchFilter(e.target.value)}
                className="pl-10 pr-9 bg-[#18181b] border-white/10 h-9" data-testid="product-search-giro" />
              {searchFilter && (
                <button
                  type="button"
                  onClick={() => { setSearchFilter(""); setInlineCatalogResults([]); }}
                  data-testid="clear-product-search"
                  aria-label="Cancella ricerca"
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-white/5 hover:bg-white/15 text-zinc-300 flex items-center justify-center transition-colors"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            <Button onClick={() => { setShowAddModal(true); setCatalogSearch(""); setCatalogResults([]); }}
              className="bg-blue-500 hover:bg-blue-600 text-white h-9 px-3 shrink-0"
              data-testid="add-extra-product-btn">
              <Plus className="w-4 h-4 mr-1" />
              <span className="text-xs">Articolo</span>
            </Button>
          </div>
        )}

        {/* Products */}
        <div className="space-y-1">
          <StopProductsList
            lockedOrder={lockedOrder}
            contrattualizzato={!!selectedStop?.sconti_fissi}
            orderItems={orderItems}
            recentOmaggi={recentOmaggi}
            stopProducts={stopProducts}
            extraProducts={extraProducts}
            filteredMain={filteredMain}
            filteredOther={filteredOther}
            showOther={showOther}
            setShowOther={setShowOther}
            showNoteFor={showNoteFor}
            setShowNoteFor={setShowNoteFor}
            showDiscountFor={showDiscountFor}
            setShowDiscountFor={setShowDiscountFor}
            scontoCanale={selectedStop?.sconto_canale || 0}
            clientDiscounts={clientDiscounts}
            onToggle={toggleProduct}
            onQtyChange={updateQty}
            onOmChange={updateOmaggio}
            onNoteChange={updateItemNote}
            onDiscountChange={updateItemDiscount}
            inlineCatalogResults={inlineCatalogResults}
            inlineCatalogSearching={inlineCatalogSearching}
            onAddCatalogProduct={addExtraProduct}
            inlineSearchActive={searchFilter.trim().length >= 2}
            lastActivatedCod={lastActivatedCod}
            onActivationConsumed={consumeLastActivated}
          />

          {/* Note generale ordine */}
          {(selectedCount > 0 || orderNote.trim() || lockedOrder) && (
            <div className="mt-4 p-3 rounded-lg border border-white/10 bg-[#18181b]" data-testid="order-note-block">
              <label className="flex items-center gap-1.5 text-[11px] text-zinc-400 mb-1.5 font-medium">
                <FileText className="w-3 h-3" />
                Note generali ordine
              </label>
              {lockedOrder ? (
                <p className="text-xs text-zinc-300 whitespace-pre-wrap">{orderNote || <span className="text-zinc-600 italic">(nessuna nota)</span>}</p>
              ) : (
                <Textarea
                  value={orderNote}
                  onChange={(e) => setOrderNote(e.target.value)}
                  rows={2}
                  placeholder="Note per il magazziniere, consegna, ecc."
                  className="bg-[#09090b] border-white/10 text-xs text-white placeholder:text-zinc-600 resize-none"
                  data-testid="order-note-input"
                />
              )}
            </div>
          )}
        </div>
      </main>

      {!lockedOrder && selectedCount > 0 && (
        <OrderBottomBar
          selectedCount={selectedCount}
          totalQty={totalQty}
          totalOm={totalOm}
          totalStima={totalStima}
          marginEur={marginEur}
          marginPct={marginPct}
          showMargin={user?.role === "admin"}
          scontoCanale={selectedStop?.sconto_canale || 0}
          saving={saving}
          editingOrderId={editingOrderId}
          onSave={saveOrder}
        />
      )}

      <AddProductModal
        open={showAddModal}
        catalogSearch={catalogSearch}
        setCatalogSearch={setCatalogSearch}
        catalogSearching={catalogSearching}
        catalogResults={catalogResults}
        stopProducts={stopProducts}
        extraProducts={extraProducts}
        onClose={() => setShowAddModal(false)}
        onAdd={addExtraProduct}
      />

      {showEstrattoConto && selectedStop?.client_id && (
        <EstrattoContoModal
          clientId={selectedStop.client_id}
          clientName={selectedStop.denominaz_1 || selectedStop.denominaz}
          onClose={() => setShowEstrattoConto(false)}
        />
      )}

      <ConfirmDialog
        open={showPriceConfirm}
        onOpenChange={setShowPriceConfirm}
        title="🔊 Conferma i prezzi per favore"
        description={
          noPriceCount === 1
            ? "C'è 1 articolo senza prezzo indicato. Imposta sconti o prezzo manuale prima di creare l'ordine, altrimenti verrà fatturato a listino pieno."
            : `Ci sono ${noPriceCount} articoli senza prezzo indicato. Imposta sconti o prezzo manuale prima di creare l'ordine, altrimenti verranno fatturati a listino pieno.`
        }
        cancelLabel="Sistema i prezzi"
        confirmLabel="Procedi comunque"
        confirmClassName="bg-red-500 hover:bg-red-600 text-white"
        confirmTestId="confirm-no-price-save"
        onConfirm={() => {
          setShowPriceConfirm(false);
          // Prima di salvare, verifica anche gli omaggi sospetti
          if (omaggiConflicts.length > 0) {
            speakAlert("Attenzione, controlla gli omaggi: possibile reso riproposto");
            setShowOmaggiConfirm(true);
            return;
          }
          saveOrder(true);
        }}
      />

      <ConfirmDialog
        open={showOmaggiConfirm}
        onOpenChange={setShowOmaggiConfirm}
        title="🔊 Controlla gli omaggi per favore"
        description={
          `Omaggio uguale (o ±1) a uno già fatto negli ultimi 18 giorni — possibile riproposta di reso da distruggere: ` +
          omaggiConflicts
            .map((c) => `${c.name} (${c.om} om. ora, ${c.prev.qta} il ${new Date(c.prev.data).toLocaleDateString("it-IT")})`)
            .join(" · ")
        }
        cancelLabel="Controlla"
        confirmLabel="Procedi comunque"
        confirmClassName="bg-red-500 hover:bg-red-600 text-white"
        confirmTestId="confirm-omaggi-save"
        onConfirm={() => {
          setShowOmaggiConfirm(false);
          saveOrder(true);
        }}
      />
    </div>
  );
}
